import React from 'react'

import { useReportDownload } from '../shared/download-report/hooks/useReportDownload'
import { useAvailableIncidentActions } from '../shared/hooks/useAvailableIncidentActions'
import { useGridTesting } from '../shared/hooks/useGridTesting'
import { useSendIncidentsToOntic } from '../shared/hooks/useSendIncidentsToOntic'
import { useSetIsIncidentReal } from '../shared/hooks/useSetIsIncidentReal'
import { IncidentTab, useTabs } from '../shared/hooks/useTabs'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { getFloorPlanHref, useNavigation } from '@/hooks/useNavigation'
import { usePermissions } from '@/hooks/usePermissions'
import { useWindowSize } from '@react-hook/window-size'

import { useDisclosure } from '@chakra-ui/react'

import {
  ActionMenu,
  AssociateIncidentIcon,
  Box,
  DarkProvider,
  DownloadIcon,
  EmergencyCallFilledIcon,
  GridTestingIcon,
  GuardsIcon,
  LocationBreadcrumb,
  NoteIcon,
  ResolveIcon,
  SOPIcon,
  SendIcon,
  ToolIcon,
  UtilityButton,
  WorkflowButton,
} from '@hivewatch/dark'
import type { ActionMenuItem as ActionMenuItemType } from '@hivewatch/dark'

import { useCall } from '@/features/emergency-call'
import { ConfirmEmergencyCallDialog } from '@/features/emergency-call/components/ConfirmEmergencyCallDialog'
import {
  StandaloneGridTestingModal,
  TailgateGridProvider,
} from '@/features/tailgate-grid'
import { IncidentQueryResult } from '@/graphql/generated/hooks'
import {
  DeviceType,
  FacilityType,
  IncidentStatus,
  IncidentWorkflowStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeSOPIncidentDrawer,
  closeTraceLogDrawer,
  closeWorkflowDrawer,
  openDeviceDrawer,
  selectSOPIncidentDrawerState,
  selectWorkflowDrawerIsOpen,
  setDevice,
  toggleSOPIncidentDrawer,
  toggleWorkflowDrawer,
} from '@/redux/ui/uiSlice'
import { GTZ_ENTER } from '@/utils/analytics'
import { isDeviceDrawerSupported } from '@/utils/devices'

import { DarkAddNote } from '../add-note'
import { AssociateCaseModal } from '../associate-case'
import { DarkGuardPanel } from '../guard-action'
import { DispatchWhiteListAlert } from '../guard-action/components/DispatchWhiteListAlert'
import { OrderRepair } from '../order-repair'
import { DarkResolve, DarkResolveToggle } from '../resolve'
import type { WorkflowReportDataI } from '../shared/download-report/types/types'
import { MixpanelDataIProps } from '../shared/types/types'
import { SOP_DRAWER_BREAKPOINT } from '../sop-incident-drawer/components/SOPIncidentDrawer'
import { WORKFLOW_DRAWER_BREAKPOINT } from '../workflow-drawer'
import {
  AiOperatorActionIcon,
  AioEvalResultsModals,
  useAioEvalResults,
} from './AioEvalResultsModal'
import { AnimatedPanel } from './AnimatedPanel'

const KebabHorizontalIcon = ({
  size = 16,
  color,
}: {
  size?: number
  color?: string
}) => (
  <svg
    fill={color ?? 'currentColor'}
    height={size}
    viewBox='0 0 20 20'
    width={size}
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle cx='2' cy='10' r='2' />
    <circle cx='10' cy='10' r='2' />
    <circle cx='18' cy='10' r='2' />
  </svg>
)

const ACTION_BUTTON_SIZE = { width: '50px', height: '46px' }

// -- Main component --

interface DarkIncidentActionsIProps {
  hasWorkflows: boolean
  incident: IncidentQueryResult['data']['incident']
  isLoading: boolean
  isLoadingWorkflow?: boolean
  isWorkflowFeatureEnabled: boolean
  mixpanelData?: MixpanelDataIProps
  onClose: () => void
  refetchFeedItems?: () => void
  workflowReportData?: WorkflowReportDataI
  workflowStatus: IncidentWorkflowStatus | null
}

export const DarkIncidentActions = ({
  hasWorkflows,
  incident,
  isLoading,
  isLoadingWorkflow = false,
  isWorkflowFeatureEnabled,
  mixpanelData,
  onClose,
  refetchFeedItems,
  workflowReportData,
  workflowStatus,
}: DarkIncidentActionsIProps) => {
  const dispatch = useAppDispatch()
  const colorScheme = useLightMode()
  const sopDrawerState = useAppSelector(selectSOPIncidentDrawerState)
  const isWorkflowDrawerOpen = useAppSelector(selectWorkflowDrawerIsOpen)
  const { goToFloorPlan } = useNavigation()
  const [width] = useWindowSize()
  const isSOPDisabled = width < SOP_DRAWER_BREAKPOINT
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const shouldShowWorkflowButton = isWorkflowFeatureEnabled
  const isWorkflowTooNarrow = width < WORKFLOW_DRAWER_BREAKPOINT
  const isWorkflowDisabled = isWorkflowTooNarrow || !hasWorkflows

  const isResolveBlockedByWorkflow =
    isWorkflowFeatureEnabled &&
    workflowStatus === IncidentWorkflowStatus.InProgress

  const workflowTooltipLabel = isWorkflowTooNarrow
    ? 'A wider screen is required for Workflow functionality'
    : !hasWorkflows
    ? 'Workflow for this incident type has not been configured yet.'
    : undefined

  const { isInternalImplementer } = usePermissions()
  const { setIncidentReal, isLoading: isUpdatingIncidentReal } =
    useSetIsIncidentReal()
  const aioEval = useAioEvalResults(incident?.id)

  const gridTesting = useGridTesting(incident)

  const {
    isOpen: isAssociateCaseModalOpen,
    onOpen: onAssociateCaseModalOpen,
    onClose: onAssociateCaseModalClose,
  } = useDisclosure()

  const {
    isOpen: isEmergencyCallDialogOpen,
    onOpen: onEmergencyCallDialogOpen,
    onClose: onEmergencyCallDialogClose,
  } = useDisclosure()

  const {
    tab,
    handleAddNoteClick,
    handleDispatchClick,
    handleRadioClick,
    handleOrderRepairClick,
    handleResolveClick,
    handleSOPClick,
    closeTabs,
  } = useTabs({ mixpanelData })

  const onToggleChange = (real: boolean) => {
    setIncidentReal(incident?.id, real, true, mixpanelData)
  }

  const onActionComplete = () => {
    refetchFeedItems()
    closeTabs()
  }

  const {
    isEmergencyCallEnabled,
    isAddNoteEnabled,
    isDispatchEnabled,
    isRadioGuardsEnabled,
    isOrderRepairEnabled,
    isResolveEnabled,
    isDownloadReportEnabled,
    isSendToOnticEnabled,
    isAdditionalOptionsEnabled,
  } = useAvailableIncidentActions({ incident })

  const isDownloadReportBlockedByWorkflowLoading =
    isDownloadReportEnabled && hasWorkflows && isLoadingWorkflow
  const isDownloadReportActionEnabled =
    isDownloadReportEnabled && !isDownloadReportBlockedByWorkflowLoading
  // Emergency call. `CallFlaggedProvider` mounts no context when the
  // emergencyCall flag is off, so useCall() returns null in those environments
  // and must never be destructured (ENG-33780).
  const call = useCall()
  const isCallOpen = call?.isOpen ?? false
  const handleEmergencyCall = () => {
    call?.openCall(
      {
        incidentId: incident?.id,
        incidentDisplayId: incident?.displayId,
        facilityName: incident?.facility?.name,
        emergencyNumber: incident?.facility?.emergencyNumber,
      },
      mixpanelData
    )
    onEmergencyCallDialogClose()
  }

  // Send to Ontic
  // This component renders only on the rebrand branch of the drawer fork,
  // so the toast styling is unconditionally rebrand.
  const { send: sendToOntic } = useSendIncidentsToOntic(incident?.id, true)

  // Download report
  const { downloadReport } = useReportDownload({ incident, workflowReportData })

  // Feature flags for More menu items
  const isCaseManagementEnabled = useFeatureFlag(FeatureFlag.caseManagement)
  const isSendToOnticFeatureEnabled = useFeatureFlag(FeatureFlag.sendToOntic)
  const { shouldEnableCaseManagement, shouldEnableSendToOntic } =
    usePermissions()
  const shouldShowAssociateIncident =
    isCaseManagementEnabled && shouldEnableCaseManagement
  const shouldShowSendToOntic =
    isSendToOnticFeatureEnabled && shouldEnableSendToOntic

  // Build "More" menu actions
  const moreMenuActions: ActionMenuItemType[] = []
  if (shouldShowAssociateIncident) {
    moreMenuActions.push({
      icon: <AssociateIncidentIcon colorScheme={colorScheme} size={16} />,
      label: 'Associate Incident',
      onClick: onAssociateCaseModalOpen,
    })
  }
  if (shouldShowSendToOntic) {
    moreMenuActions.push({
      icon: <SendIcon colorScheme={colorScheme} size={16} />,
      label: 'Send to Ontic',
      onClick: () => {
        sendToOntic()
      },
      disabled: !isSendToOnticEnabled,
    })
  }
  moreMenuActions.push({
    icon: <DownloadIcon colorScheme={colorScheme} size={16} />,
    label: 'Download Report',
    onClick: downloadReport,
    disabled: !isDownloadReportActionEnabled,
  })

  // Build guards menu actions
  const guardsMenuActions: ActionMenuItemType[] = []
  guardsMenuActions.push({
    label: 'Dispatch Guards',
    onClick: handleDispatchClick,
    disabled: !isDispatchEnabled,
  })
  guardsMenuActions.push({
    label: 'Radio Guards',
    onClick: handleRadioClick,
    disabled: !isRadioGuardsEnabled,
  })

  return (
    <DarkProvider>
      <Box pb='0'>
        {/* Location breadcrumb */}
        {!isLoading && incident && (
          <LocationBreadcrumb
            colorScheme={colorScheme}
            items={[
              ...(incident.devices?.[0]?.name
                ? [
                    {
                      label: incident.devices[0].name,
                      // Omitted for types DeviceDrawer can't render (Alarm,
                      // Checkpoint) so the crumb stays copy-only.
                      onClick: isDeviceDrawerSupported(
                        incident.devices[0]?.type
                      )
                        ? () => {
                            const device = incident.devices?.[0]
                            dispatch(
                              setDevice({ id: device?.id, type: device?.type })
                            )
                            dispatch(openDeviceDrawer())
                          }
                        : undefined,
                    },
                  ]
                : []),
              ...(incident.floor?.name
                ? [
                    {
                      label: `Floor ${incident.floor.name}`,
                      href: getFloorPlanHref(incident.floor.id),
                      onClick: () => {
                        onClose()
                        requestAnimationFrame(() => {
                          setTimeout(() => {
                            goToFloorPlan(incident.floor.id)
                          }, 0)
                        })
                      },
                    },
                  ]
                : []),
              ...(incident.facility?.shortName
                ? [
                    {
                      label: incident.facility.shortName,
                      ...(incident.facility.type === FacilityType.Normal &&
                      incident.floor?.id
                        ? {
                            href: getFloorPlanHref(incident.floor.id),
                            onClick: () => {
                              onClose()
                              requestAnimationFrame(() => {
                                setTimeout(() => {
                                  goToFloorPlan(incident.floor.id)
                                }, 0)
                              })
                            },
                          }
                        : {}),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {/* Action bar */}
        <Box
          alignItems='center'
          borderBottom='1px solid'
          borderColor={`${prefix}border.subtle`}
          display='flex'
          justifyContent='space-between'
          pb='12px'
          pt='12px'
          px='24px'
          width='100%'
        >
          {/* Left: Workflow button */}
          {shouldShowWorkflowButton && (
            <WorkflowButton
              colorScheme={colorScheme}
              disabled={isWorkflowDisabled}
              isActive={isWorkflowDrawerOpen}
              onClick={() => {
                dispatch(closeSOPIncidentDrawer())
                dispatch(closeTraceLogDrawer())
                dispatch(toggleWorkflowDrawer())
              }}
              tooltipLabel={
                isWorkflowDisabled ? workflowTooltipLabel : undefined
              }
            />
          )}

          {/* Center: Resolve toggle (resolved only) */}
          {incident?.status === IncidentStatus.Resolved && (
            <DarkResolveToggle
              colorScheme={colorScheme}
              isLoading={isUpdatingIncidentReal}
              isReal={incident?.real}
              onChange={onToggleChange}
            />
          )}

          {/* Right: Action buttons */}
          <Box alignItems='center' display='flex' gap='8px'>
            {/* Add Note */}
            {isAddNoteEnabled && (
              <UtilityButton
                colorScheme={colorScheme}
                icon={<NoteIcon size={18} />}
                isSelected={tab === IncidentTab.AddNote}
                label='Add Note'
                onClick={handleAddNoteClick}
                stylesOverride={ACTION_BUTTON_SIZE}
                tooltipLabel='Add Note'
              />
            )}

            {/* Standard Procedures */}
            <UtilityButton
              colorScheme={colorScheme}
              disabled={isSOPDisabled}
              icon={<SOPIcon size={18} />}
              isSelected={sopDrawerState?.isOpen === true}
              label='Standard Procedures'
              onClick={() => {
                handleSOPClick()
                dispatch(closeTraceLogDrawer())
                dispatch(closeWorkflowDrawer())
                dispatch(toggleSOPIncidentDrawer())
              }}
              stylesOverride={ACTION_BUTTON_SIZE}
              tooltipLabel='Standard Procedures'
            />

            {/* Assign Guards (dropdown) */}
            {(isDispatchEnabled || isRadioGuardsEnabled) && (
              <ActionMenu
                colorScheme={colorScheme}
                items={guardsMenuActions}
                trigger={({ onClick, isOpen: menuOpen }) => (
                  <UtilityButton
                    colorScheme={colorScheme}
                    icon={<GuardsIcon size={18} />}
                    isSelected={
                      menuOpen ||
                      tab === IncidentTab.DispatchGuards ||
                      tab === IncidentTab.RadioGuards
                    }
                    label='Assign Guards'
                    onClick={onClick}
                    stylesOverride={ACTION_BUTTON_SIZE}
                    tooltipLabel='Assign Guards'
                  />
                )}
              />
            )}

            {/* Order Repair */}
            {isOrderRepairEnabled && (
              <UtilityButton
                colorScheme={colorScheme}
                icon={<ToolIcon size={18} />}
                isSelected={tab === IncidentTab.OrderRepair}
                label='Order Repair'
                onClick={handleOrderRepairClick}
                stylesOverride={ACTION_BUTTON_SIZE}
                tooltipLabel='Order Repair'
              />
            )}

            {/* Emergency Call */}
            {isEmergencyCallEnabled && (
              <UtilityButton
                colorScheme={colorScheme}
                disabled={isCallOpen}
                icon={<EmergencyCallFilledIcon size={18} />}
                iconColor='var(--hw-dark-colors-interactive-danger, #E53E3E)'
                isSelected={false}
                label='Emergency Call'
                onClick={onEmergencyCallDialogOpen}
                stylesOverride={ACTION_BUTTON_SIZE}
                tooltipLabel='Emergency Call'
              />
            )}

            {/* Resolve */}
            {isResolveEnabled && (
              <UtilityButton
                colorScheme={colorScheme}
                disabled={isResolveBlockedByWorkflow}
                icon={<ResolveIcon size={18} />}
                isSelected={tab === IncidentTab.Resolve}
                label='Resolve'
                onClick={handleResolveClick}
                stylesOverride={ACTION_BUTTON_SIZE}
                tooltipLabel={
                  isResolveBlockedByWorkflow
                    ? 'Cannot resolve until workflow complete.'
                    : 'Resolve'
                }
              />
            )}

            {/* Grid Testing */}
            {gridTesting.isEnabled && (
              <UtilityButton
                colorScheme={colorScheme}
                data-testid='open-grid-testing-zone-button'
                icon={<GridTestingIcon size={18} />}
                isSelected={false}
                label='Enter Grid Testing Zone'
                onClick={() => {
                  gridTesting.trackTailgateEvent(GTZ_ENTER)
                  gridTesting.open()
                }}
                stylesOverride={ACTION_BUTTON_SIZE}
                tooltipLabel='Enter Grid Testing Zone'
              />
            )}

            {/* AIO Eval */}
            {isInternalImplementer && (
              <UtilityButton
                colorScheme={colorScheme}
                data-testid='aio-eval-results-action-button'
                disabled={!incident?.id}
                icon={<AiOperatorActionIcon />}
                isSelected={false}
                label='AIO Evaluation Results'
                onClick={aioEval.handleOpen}
                stylesOverride={ACTION_BUTTON_SIZE}
                tooltipLabel='AIO Evaluation Results'
              />
            )}

            {/* More menu */}
            {(isAdditionalOptionsEnabled || shouldShowSendToOntic) && (
              <ActionMenu
                anchorRight
                colorScheme={colorScheme}
                items={moreMenuActions}
                trigger={({ onClick, isOpen: menuOpen }) => (
                  <UtilityButton
                    colorScheme={colorScheme}
                    icon={<KebabHorizontalIcon size={16} />}
                    isSelected={menuOpen}
                    label='More'
                    onClick={onClick}
                    stylesOverride={ACTION_BUTTON_SIZE}
                    tooltipLabel='More'
                  />
                )}
              />
            )}
          </Box>
        </Box>

        {/* Animated panels */}
        <Box>
          {tab === IncidentTab.AddNote && (
            <AnimatedPanel>
              <DarkAddNote
                colorScheme={colorScheme}
                incidentId={incident?.id}
                mixpanelData={mixpanelData}
                onActionComplete={closeTabs}
              />
            </AnimatedPanel>
          )}
          {tab === IncidentTab.DispatchGuards && (
            <AnimatedPanel>
              <DispatchWhiteListAlert incident={incident} />
              <DarkGuardPanel
                colorScheme={colorScheme}
                incident={incident}
                mixpanelData={mixpanelData}
                onActionComplete={onActionComplete}
              />
            </AnimatedPanel>
          )}
          {tab === IncidentTab.RadioGuards && (
            <AnimatedPanel>
              <DarkGuardPanel
                colorScheme={colorScheme}
                incident={incident}
                mixpanelData={mixpanelData}
                mode='radio'
                onActionComplete={onActionComplete}
              />
            </AnimatedPanel>
          )}
          {tab === IncidentTab.OrderRepair && (
            <AnimatedPanel>
              <OrderRepair
                deviceType={gridTesting.device?.type}
                facilityId={incident?.facility?.id}
                incidentId={incident?.id}
                incidentStatus={incident?.status}
                mixpanelData={mixpanelData}
                onActionComplete={onActionComplete}
              />
            </AnimatedPanel>
          )}
          {tab === IncidentTab.Resolve && (
            <AnimatedPanel>
              <DarkResolve
                colorScheme={colorScheme}
                incident={incident}
                mixpanelData={mixpanelData}
                onActionComplete={closeTabs}
              />
            </AnimatedPanel>
          )}
        </Box>

        {/* Modals */}
        <AssociateCaseModal
          incidentId={incident?.id}
          isOpen={isAssociateCaseModalOpen}
          onClose={onAssociateCaseModalClose}
        />
        {isInternalImplementer && (
          <AioEvalResultsModals
            error={aioEval.error}
            evaluations={aioEval.evaluations}
            isLoading={aioEval.isLoading}
            isOpen={aioEval.isResultsOpen}
            onClose={aioEval.onResultsClose}
          />
        )}
        {isEmergencyCallDialogOpen && (
          <ConfirmEmergencyCallDialog
            emergencyNumber={incident?.facility?.emergencyNumber}
            handleOnClick={handleEmergencyCall}
            isOpen={isEmergencyCallDialogOpen}
            onClose={onEmergencyCallDialogClose}
          />
        )}
        {gridTesting.isEnabled &&
          gridTesting.gridCameraId &&
          gridTesting.device?.id && (
            <TailgateGridProvider
              associateDeviceValues={{
                cameraId: gridTesting.gridCameraId,
                cameraName: gridTesting.gridCameraName,
                doorId: gridTesting.device.id,
                doorName: gridTesting.device.name ?? '',
                deviceType: DeviceType.Door,
              }}
              isGridConfigured={gridTesting.isGridConfigured}
              isTailgateGridOpen={gridTesting.isOpen}
              onTailgateGridClose={gridTesting.close}
              shouldRenderDummyPlayer={gridTesting.isOpen}
              trackTailgateEvent={gridTesting.trackTailgateEvent}
            >
              <StandaloneGridTestingModal
                colorScheme={colorScheme}
                isOpen={gridTesting.isOpen}
                onClose={gridTesting.close}
                presetIncident={gridTesting.presetIncident}
                zIndex={1500}
              />
            </TailgateGridProvider>
          )}
      </Box>
    </DarkProvider>
  )
}
